import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, RefreshCw, AlertCircle, Pencil, KeyRound, UserX, UserCheck, Trash2, X, Save, UserPlus,
} from 'lucide-react';
import { usersAPI } from '../../services/api';
import { useAppContext } from '../../context/AppContext';

const ROLES = ['Administrator', 'Instructor', 'Trainee'];
const STATUSES = ['Active', 'Inactive'];

const UsersTab = () => {
  const { showNotification, currentUser } = useAppContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [creating, setCreating] = useState(null);
  const [createSaving, setCreateSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue, setPwValue] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [actingId, setActingId] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await usersAPI.getUsers();
      const list = res.data?.data || res.data || [];
      setUsers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (!q) return true;
      const haystack = [u.name, u.firstName, u.lastName, u.email, u.department, u.specialization]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [users, search, roleFilter, statusFilter]);

  const openCreate = () => {
    setCreating({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: 'Trainee',
      status: 'Active',
      department: '',
      phone: '',
      location: '',
      specialization: '',
      sendWelcomeEmail: false,
    });
  };

  const handleCreateSave = async () => {
    if (!creating) return;
    const c = creating;
    if (!c.firstName.trim() || !c.lastName.trim() || !c.email.trim()) {
      showNotification('First name, last name and email are required', 'error');
      return;
    }
    if (c.password.length < 6) {
      showNotification('Password must be at least 6 characters', 'error');
      return;
    }
    if (c.password !== c.confirmPassword) {
      showNotification('Passwords do not match', 'error');
      return;
    }
    setCreateSaving(true);
    try {
      await usersAPI.createUser({
        firstName: c.firstName.trim(),
        lastName: c.lastName.trim(),
        email: c.email.trim(),
        password: c.password,
        role: c.role,
        status: c.status,
        department: c.department,
        phone: c.phone,
        location: c.location,
        specialization: c.specialization,
        sendWelcomeEmail: c.sendWelcomeEmail,
      });
      showNotification('User created', 'success');
      setCreating(null);
      loadUsers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to create user', 'error');
    } finally {
      setCreateSaving(false);
    }
  };

  const openEdit = (u) => {
    setEditing({
      id: u.id,
      firstName: u.firstName || '',
      lastName: u.lastName || '',
      email: u.email || '',
      role: u.role || 'Trainee',
      status: u.status || 'Active',
      department: u.department || '',
      phone: u.phone || '',
      location: u.location || '',
      specialization: u.specialization || '',
    });
  };

  const handleEditSave = async () => {
    if (!editing) return;
    if (!editing.firstName.trim() || !editing.lastName.trim() || !editing.email.trim()) {
      showNotification('First name, last name and email are required', 'error');
      return;
    }
    setEditSaving(true);
    try {
      await usersAPI.updateUser(editing.id, {
        firstName: editing.firstName.trim(),
        lastName: editing.lastName.trim(),
        email: editing.email.trim(),
        role: editing.role,
        status: editing.status,
        department: editing.department,
        phone: editing.phone,
        location: editing.location,
        specialization: editing.specialization,
      });
      showNotification('User updated', 'success');
      setEditing(null);
      loadUsers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to update user', 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const toggleStatus = async (u) => {
    if (currentUser && (currentUser.id === u.id || currentUser.userId === u.id)) {
      showNotification('You cannot change your own status', 'error');
      return;
    }
    setActingId(u.id);
    try {
      const nextStatus = u.status === 'Active' ? 'Inactive' : 'Active';
      await usersAPI.updateUser(u.id, { status: nextStatus });
      showNotification(`User ${nextStatus === 'Active' ? 'activated' : 'deactivated'}`, 'success');
      loadUsers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to change status', 'error');
    } finally {
      setActingId(null);
    }
  };

  const handlePwSave = async () => {
    if (!pwTarget) return;
    if (pwValue.length < 6) {
      showNotification('Password must be at least 6 characters', 'error');
      return;
    }
    if (pwValue !== pwConfirm) {
      showNotification('Passwords do not match', 'error');
      return;
    }
    setPwSaving(true);
    try {
      await usersAPI.adminResetPassword(pwTarget.id, pwValue);
      showNotification(`Password reset for ${pwTarget.name || pwTarget.email}`, 'success');
      setPwTarget(null);
      setPwValue('');
      setPwConfirm('');
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to reset password', 'error');
    } finally {
      setPwSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    try {
      await usersAPI.permanentDeleteUser(deleteTarget.id);
      showNotification('User permanently deleted', 'success');
      setDeleteTarget(null);
      setDeleteConfirmText('');
      loadUsers();
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to delete user', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="settings__users animate-fade-in">
      <div className="card">
        <div className="card-header" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'center' }}>
          <div style={{ flex: '1 1 220px' }}>
            <h3>Users</h3>
            <p className="text-sm text-muted">Manage profiles, roles, status and access for all platform users.</p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
              <input
                className="form-input"
                placeholder="Search name, email, dept..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: 30, minWidth: 220 }}
              />
            </div>
            <select className="form-input" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} style={{ minWidth: 140 }}>
              <option value="all">All roles</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="form-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ minWidth: 130 }}>
              <option value="all">All status</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button className="btn btn-outline btn-sm" onClick={loadUsers} disabled={loading} title="Refresh">
              <RefreshCw size={14} className={loading ? 'settings__spin' : ''} />
            </button>
            <button className="btn btn-primary btn-sm" onClick={openCreate}>
              <UserPlus size={14} /> New user
            </button>
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 'var(--space-8) 0' }}>
            <RefreshCw size={20} className="settings__spin" style={{ margin: '0 auto' }} />
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>Loading users...</p>
          </div>
        )}

        {error && !loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-4)', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-md)', color: '#b91c1c', margin: 'var(--space-4)' }}>
            <AlertCircle size={16} />
            <span style={{ flex: 1, fontSize: 'var(--font-size-sm)' }}>{error}</span>
            <button className="btn btn-outline btn-sm" onClick={loadUsers}>Retry</button>
          </div>
        )}

        {!loading && !error && (
          <div className="settings__users-table">
            <div className="settings__users-header">
              <span>User</span>
              <span>Role</span>
              <span>Department</span>
              <span>Status</span>
              <span style={{ textAlign: 'right' }}>Actions</span>
            </div>
            {filtered.length === 0 ? (
              <div style={{ padding: 'var(--space-6)', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                No users match the current filters.
              </div>
            ) : filtered.map((u) => {
              const isSelf = currentUser && (currentUser.id === u.id || currentUser.userId === u.id);
              return (
                <div key={u.id} className="settings__users-row">
                  <div className="settings__users-user">
                    <div className="avatar" style={{ width: 36, height: 36, fontSize: '0.75rem' }}>
                      {u.initials || (u.name || '?').charAt(0)}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span className="settings__users-name">{u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim()}</span>
                      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</span>
                    </div>
                  </div>
                  <span>
                    <span className={`badge ${u.role === 'Administrator' ? 'badge-purple' : u.role === 'Instructor' ? 'badge-blue' : 'badge-green'}`}>
                      {u.role}
                    </span>
                  </span>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>{u.department || '—'}</span>
                  <span>
                    <span className={`badge ${u.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>{u.status}</span>
                  </span>
                  <div className="settings__users-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)} title="Edit profile">
                      <Pencil size={13} />
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setPwTarget(u); setPwValue(''); setPwConfirm(''); }} title="Reset password">
                      <KeyRound size={13} />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => toggleStatus(u)}
                      disabled={isSelf || actingId === u.id}
                      title={u.status === 'Active' ? 'Deactivate' : 'Activate'}
                    >
                      {u.status === 'Active' ? <UserX size={13} /> : <UserCheck size={13} />}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => { setDeleteTarget(u); setDeleteConfirmText(''); }}
                      disabled={isSelf}
                      title="Permanently delete"
                      style={{ color: '#b91c1c' }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {creating && (
        <Modal title="Create new user" onClose={() => setCreating(null)}>
          <div className="settings__users-form">
            <Field label="First name *"><input className="form-input" value={creating.firstName} onChange={(e) => setCreating({ ...creating, firstName: e.target.value })} autoFocus /></Field>
            <Field label="Last name *"><input className="form-input" value={creating.lastName} onChange={(e) => setCreating({ ...creating, lastName: e.target.value })} /></Field>
            <Field label="Email *"><input className="form-input" type="email" value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} /></Field>
            <Field label="Role">
              <select className="form-input" value={creating.role} onChange={(e) => setCreating({ ...creating, role: e.target.value })}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Password * (min 6 chars)"><input className="form-input" type="password" value={creating.password} onChange={(e) => setCreating({ ...creating, password: e.target.value })} /></Field>
            <Field label="Confirm password *"><input className="form-input" type="password" value={creating.confirmPassword} onChange={(e) => setCreating({ ...creating, confirmPassword: e.target.value })} /></Field>
            <Field label="Status">
              <select className="form-input" value={creating.status} onChange={(e) => setCreating({ ...creating, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Department"><input className="form-input" value={creating.department} onChange={(e) => setCreating({ ...creating, department: e.target.value })} /></Field>
            <Field label="Phone"><input className="form-input" value={creating.phone} onChange={(e) => setCreating({ ...creating, phone: e.target.value })} /></Field>
            <Field label="Location"><input className="form-input" value={creating.location} onChange={(e) => setCreating({ ...creating, location: e.target.value })} /></Field>
            <Field label="Specialization" full><input className="form-input" value={creating.specialization} onChange={(e) => setCreating({ ...creating, specialization: e.target.value })} /></Field>
            <label className="settings__users-field" style={{ gridColumn: '1 / -1', flexDirection: 'row', alignItems: 'center', gap: 'var(--space-2)' }}>
              <input type="checkbox" checked={creating.sendWelcomeEmail} onChange={(e) => setCreating({ ...creating, sendWelcomeEmail: e.target.checked })} />
              <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>Send welcome email with login details</span>
            </label>
          </div>
          <div className="settings__users-modal-actions">
            <button className="btn btn-outline" onClick={() => setCreating(null)} disabled={createSaving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreateSave} disabled={createSaving}>
              {createSaving ? <><RefreshCw size={14} className="settings__spin" /> Creating</> : <><UserPlus size={14} /> Create user</>}
            </button>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal title="Edit user" onClose={() => setEditing(null)}>
          <div className="settings__users-form">
            <Field label="First name *"><input className="form-input" value={editing.firstName} onChange={(e) => setEditing({ ...editing, firstName: e.target.value })} /></Field>
            <Field label="Last name *"><input className="form-input" value={editing.lastName} onChange={(e) => setEditing({ ...editing, lastName: e.target.value })} /></Field>
            <Field label="Email *"><input className="form-input" type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></Field>
            <Field label="Role">
              <select className="form-input" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select className="form-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Department"><input className="form-input" value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })} /></Field>
            <Field label="Phone"><input className="form-input" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></Field>
            <Field label="Location"><input className="form-input" value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></Field>
            <Field label="Specialization" full><input className="form-input" value={editing.specialization} onChange={(e) => setEditing({ ...editing, specialization: e.target.value })} /></Field>
          </div>
          <div className="settings__users-modal-actions">
            <button className="btn btn-outline" onClick={() => setEditing(null)} disabled={editSaving}>Cancel</button>
            <button className="btn btn-primary" onClick={handleEditSave} disabled={editSaving}>
              {editSaving ? <><RefreshCw size={14} className="settings__spin" /> Saving</> : <><Save size={14} /> Save changes</>}
            </button>
          </div>
        </Modal>
      )}

      {pwTarget && (
        <Modal title={`Reset password for ${pwTarget.name || pwTarget.email}`} onClose={() => setPwTarget(null)}>
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
            The user will need to use the new password on their next login. They are not notified automatically.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <Field label="New password (min 6 chars)" full>
              <input className="form-input" type="password" value={pwValue} onChange={(e) => setPwValue(e.target.value)} autoFocus />
            </Field>
            <Field label="Confirm password" full>
              <input className="form-input" type="password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} />
            </Field>
          </div>
          <div className="settings__users-modal-actions">
            <button className="btn btn-outline" onClick={() => setPwTarget(null)} disabled={pwSaving}>Cancel</button>
            <button className="btn btn-primary" onClick={handlePwSave} disabled={pwSaving}>
              {pwSaving ? <><RefreshCw size={14} className="settings__spin" /> Saving</> : 'Set new password'}
            </button>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal title="Permanently delete user" onClose={() => setDeleteTarget(null)}>
          <div style={{ padding: 'var(--space-3)', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-md)', color: '#991b1b', marginBottom: 'var(--space-3)', fontSize: 'var(--font-size-sm)' }}>
            <strong>This action cannot be undone.</strong> Deleting <strong>{deleteTarget.name || deleteTarget.email}</strong> will remove their account, enrollments, assignments, analytics events and notifications. Uploaded content will remain but lose its uploader reference. To deactivate the account instead, close this dialog and use the deactivate action.
          </div>
          <Field label='Type DELETE to confirm' full>
            <input className="form-input" value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder="DELETE" autoFocus />
          </Field>
          <div className="settings__users-modal-actions">
            <button className="btn btn-outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
            <button
              className="btn"
              onClick={handleDelete}
              disabled={deleting || deleteConfirmText !== 'DELETE'}
              style={{ background: '#b91c1c', color: '#fff' }}
            >
              {deleting ? <><RefreshCw size={14} className="settings__spin" /> Deleting</> : <><Trash2 size={14} /> Delete permanently</>}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <label className="settings__users-field" style={full ? { gridColumn: '1 / -1' } : undefined}>
    <span>{label}</span>
    {children}
  </label>
);

const Modal = ({ title, children, onClose }) => (
  <div className="settings__users-modal-backdrop" onClick={onClose}>
    <div className="settings__users-modal" onClick={(e) => e.stopPropagation()}>
      <div className="settings__users-modal-header">
        <h3>{title}</h3>
        <button className="btn-icon" onClick={onClose}><X size={16} /></button>
      </div>
      <div className="settings__users-modal-body">{children}</div>
    </div>
  </div>
);

export default UsersTab;
